import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App.tsx';
import reportWebVitals from "./reportWebVitals.js";
import { BrowserRouter } from 'react-router-dom';

// ============================================================================
// SECURITY: Disable console logging in production
// ============================================================================
// This prevents sensitive information disclosure in browser console
// CWE-200: Exposure of Sensitive Information to an Unauthorized Actor
// ============================================================================

// Comprehensive URL sanitization function
// Handles strings, Error objects, stack traces, and nested objects
const sanitizeForLogging = (input) => {
  if (input === null || input === undefined) {
    return input;
  }
  
  // URL pattern matching - matches full URLs including paths and query strings
  const urlPattern = /https?:\/\/[^\s\)\]\}\"\'\,]+/gi;
  const domainPattern = /[a-zA-Z0-9-]+\.(azurecontainerapps|onrender|herokuapp|vercel|netlify|pages\.dev|amazonaws|cloudfront)[^\s\)\]\}\"\'\,]*/gi;
  
  // Handle strings
  if (typeof input === 'string') {
    return input
      // Remove full URLs (http:// and https://) - matches entire URL including path
      .replace(urlPattern, '[API_ENDPOINT]')
      // Remove domain names (even without protocol)
      .replace(domainPattern, '[BACKEND_HOST]')
      // Remove tokens
      .replace(/token[=:]\s*["']?([a-zA-Z0-9_-]+)["']?/gi, 'token=[REDACTED]')
      // Remove user IDs
      .replace(/user[_\s]?id[=:]\s*\d+/gi, 'user_id=[REDACTED]')
      // Remove passwords
      .replace(/password[=:]\s*["']?[^"'\s]+["']?/gi, 'password=[REDACTED]')
      // Remove API keys
      .replace(/api[_\s]?key[=:]\s*["']?[^"'\s]+["']?/gi, 'api_key=[REDACTED]')
      // Remove authorization headers
      .replace(/authorization[=:]\s*["']?[^"'\s]+["']?/gi, 'authorization=[REDACTED]');
  }
  
  // Handle Error objects
  if (input instanceof Error) {
    // Create a new error with sanitized message
    const sanitizedMessage = sanitizeForLogging(input.message);
    const sanitizedError = new Error(sanitizedMessage);
    sanitizedError.name = input.name;
    
    // Sanitize stack trace (contains file paths and URLs)
    if (input.stack) {
      sanitizedError.stack = sanitizeForLogging(input.stack);
    }
    
    // Preserve other error properties
    if (input.cause) {
      sanitizedError.cause = sanitizeForLogging(input.cause);
    }
    
    return sanitizedError;
  }
  
  // Handle objects
  if (typeof input === 'object' && input !== null) {
    // Handle arrays
    if (Array.isArray(input)) {
      return input.map(item => sanitizeForLogging(item));
    }
    
    // Handle plain objects
    const sanitized = {};
    for (const key in input) {
      if (Object.prototype.hasOwnProperty.call(input, key)) {
        const lowerKey = key.toLowerCase();
        // Skip sensitive keys entirely
        if (lowerKey.includes('token') || 
            lowerKey.includes('password') || 
            lowerKey.includes('secret') ||
            lowerKey.includes('key') ||
            lowerKey.includes('authorization')) {
          sanitized[key] = '[REDACTED]';
        } else if (lowerKey.includes('url') || lowerKey.includes('endpoint') || lowerKey.includes('href')) {
          sanitized[key] = '[URL_REDACTED]';
        } else {
          sanitized[key] = sanitizeForLogging(input[key]);
        }
      }
    }
    return sanitized;
  }
  
  return input;
};

if (process.env.NODE_ENV === 'production') {
  // STEP 1: Override console methods FIRST to catch all logging
  const originalError = console.error;
  const originalWarn = console.warn;
  
  // Completely disable debug/info/log in production
  console.log = function() {};
  console.debug = function() {};
  console.info = function() {};
  console.table = function() {};
  console.group = function() {};
  console.groupEnd = function() {};
  console.groupCollapsed = function() {};
  console.trace = function() {};
  
  // Enhanced sanitization for warn and error
  const sanitizeAndLog = (originalMethod, args) => {
    const sanitized = args.map(arg => sanitizeForLogging(arg));
    originalMethod.apply(console, sanitized);
  };
  
  console.warn = function(...args) {
    sanitizeAndLog(originalWarn, args);
  };
  
  console.error = function(...args) {
    sanitizeAndLog(originalError, args);
  };
  
  // STEP 2: Override Error.prototype to sanitize URLs in all errors
  const originalErrorToString = Error.prototype.toString;
  Error.prototype.toString = function() {
    const originalString = originalErrorToString.call(this);
    return sanitizeForLogging(originalString);
  };
  
  // Override Error.prototype.stack getter to sanitize stack traces
  const originalStackDescriptor = Object.getOwnPropertyDescriptor(Error.prototype, 'stack');
  if (originalStackDescriptor) {
    Object.defineProperty(Error.prototype, 'stack', {
      get: function() {
        const originalStack = originalStackDescriptor.get ? originalStackDescriptor.get.call(this) : this._stack || '';
        return sanitizeForLogging(originalStack);
      },
      set: function(value) {
        this._stack = value;
        if (originalStackDescriptor.set) {
          originalStackDescriptor.set.call(this, value);
        }
      },
      configurable: true,
      enumerable: false
    });
  }
  
  // STEP 3: Wrap fetch globally to intercept and sanitize errors
  // NOTE: Browser's automatic network logging cannot be prevented,
  // but we sanitize all JavaScript errors and stack traces
  const originalFetch = window.fetch;
  window.fetch = function(input, init) {
    // Call original fetch
    const fetchPromise = originalFetch.apply(this, arguments);
    
    // Intercept the promise to catch and sanitize errors
    return fetchPromise
      .then(response => {
        // If response is not OK, create sanitized error immediately
        if (!response.ok) {
          const clonedResponse = response.clone();
          
          // Create sanitized error that doesn't expose the URL
          const sanitizedError = new Error(`API request failed with status ${response.status}`);
          sanitizedError.name = 'FetchError';
          sanitizedError.status = response.status;
          sanitizedError.statusText = response.statusText;
          sanitizedError.response = clonedResponse;
          
          // Override toString to prevent URL exposure
          sanitizedError.toString = function() {
            return `FetchError: API request failed with status ${this.status}`;
          };
          
          // Override stack to sanitize
          Object.defineProperty(sanitizedError, 'stack', {
            get: function() {
              return sanitizeForLogging(`FetchError: API request failed with status ${this.status}\n    at [API_ENDPOINT]`);
            },
            configurable: true
          });
          
          return Promise.reject(sanitizedError);
        }
        return response;
      })
      .catch(error => {
        // Sanitize any fetch errors
        const sanitizedMessage = sanitizeForLogging(error.message || 'Network error');
        const sanitizedError = new Error(sanitizedMessage);
        sanitizedError.name = error.name || 'FetchError';
        
        // Sanitize stack trace
        if (error.stack) {
          Object.defineProperty(sanitizedError, 'stack', {
            get: function() {
              return sanitizeForLogging(error.stack);
            },
            configurable: true
          });
        }
        
        // Override toString
        sanitizedError.toString = function() {
          return sanitizeForLogging(this.message);
        };
        
        throw sanitizedError;
      });
  };
  
  // STEP 4: Override global error handlers
  const originalWindowError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    const sanitizedMessage = sanitizeForLogging(message);
    const sanitizedSource = sanitizeForLogging(source);
    const sanitizedError = error ? sanitizeForLogging(error) : null;
    
    if (originalWindowError) {
      return originalWindowError.call(window, sanitizedMessage, sanitizedSource, lineno, colno, sanitizedError);
    }
    return false;
  };
  
  const originalUnhandledRejection = window.onunhandledrejection;
  window.onunhandledrejection = function(event) {
    if (event && event.reason) {
      event.reason = sanitizeForLogging(event.reason);
    }
    if (originalUnhandledRejection) {
      return originalUnhandledRejection.call(window, event);
    }
    return false;
  };
  
  // One-time security notice
  originalError('%c[SECURITY]', 'color: red; font-weight: bold;', 'Production console logging disabled and sanitized for security');
}

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
